/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ApolloCache } from "@apollo/client";
import dayjs from "dayjs";
import {
  BusinessContextTargetsQuery,
  BusinessContextTargetsQueryVariables,
  BusinessContextKpis,
  BusinessContext,
} from "~/generated/graphql";
import { KpiTarget } from "~/components/KPI/types";

enum KpiField {
  COST_ALLOCATION_COVERAGE = "costAllocationCoverage",
  NON_PROD_HOURS = "nonProdHours",
  OVERALL_WASTE = "overallWaste",
}

type KpiFields = Extract<keyof BusinessContextKpis, `${KpiField}`>;

export function getModifiedKpiName(
  queryVariables: BusinessContextTargetsQueryVariables
): KpiFields {
  const { fetchCostAllocation, fetchNonProdHours } = queryVariables;

  if (fetchCostAllocation) {
    return KpiField.COST_ALLOCATION_COVERAGE;
  }
  if (fetchNonProdHours) {
    return KpiField.NON_PROD_HOURS;
  }

  return KpiField.OVERALL_WASTE;
}

export function removeTargetFromCache(
  bcTargets: BusinessContextTargetsQuery | null,
  id: string,
  modifiedKpi: KpiFields
) {
  const kpis = bcTargets?.businessContext?.kpis;

  return {
    businessContext: {
      id: bcTargets?.businessContext?.id ?? "",
      kpis: {
        ...kpis,
        [modifiedKpi]: {
          ...kpis?.[modifiedKpi],
          targets: (kpis?.[modifiedKpi]?.targets ?? []).filter(
            (target) => target?.id !== id
          ),
        },
      },
    },
  };
}

export function addTargetToCache(
  bcTargets: BusinessContextTargetsQuery | null,
  addedTarget: KpiTarget,
  modifiedKpi: KpiFields
) {
  const kpis = bcTargets?.businessContext?.kpis;

  return {
    businessContext: {
      ...bcTargets?.businessContext,
      id: bcTargets?.businessContext?.id ?? "",
      kpis: {
        ...kpis,
        [modifiedKpi]: {
          ...kpis?.[modifiedKpi],
          targets: [addedTarget, ...(kpis?.[modifiedKpi]?.targets ?? [])],
        },
      },
    },
  };
}

export function invalidateKpisInCache(
  cache: ApolloCache<unknown>,
  variables: BusinessContextTargetsQueryVariables
) {
  cache.modify<BusinessContext>({
    id: `BusinessContext:${variables.bcId}`,
    fields: {
      /* eslint-disable @typescript-eslint/naming-convention
        -- DELETE comes directly from Apollo and has `any` as a type */
      kpis: (kpis, { DELETE }) => {
        return {
          ...kpis,
          [getModifiedKpiName(variables)]: DELETE,
        };
      },
    },
  });
}
/* eslint-enable @typescript-eslint/naming-convention */

export function isCurrentTarget({ startDate, endDate }: KpiTarget) {
  return dayjs().isAfter(dayjs(startDate)) && dayjs().isBefore(dayjs(endDate));
}
